import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { ArrowLeft, ArrowRight, Loader2, Phone, Mail, MapPin, Check } from "lucide-react";
import Navigation from "@/components/site/Navigation";
import Footer from "@/components/site/Footer";
import { API } from "@/lib/api";

const SUBJECTS = [
  "General Enquiry",
  "Request a Quote",
  "Existing Client Support",
  "Partnerships",
  "Press / Media",
  "Other",
];

const initial = {
  full_name: "",
  email: "",
  phone: "",
  subject: SUBJECTS[0],
  message: "",
};

export default function ContactUs() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.full_name || !form.email || !form.subject || !form.message) {
      toast.error("Please complete the required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact-messages`, form);
      setSent(true);
      setForm(initial);
      toast.success("Message received — we'll be in touch shortly.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send your message. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Could not send your message.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full bg-white border border-[#E5EBF2] rounded-none px-4 py-3.5 text-[#050B14] text-base placeholder:text-[#A3A3A3] focus:border-[#0E2A57] focus:ring-1 focus:ring-[#0E2A57] focus:outline-none transition";

  return (
    <div data-testid="contact-page" className="bg-white text-[#050B14]">
      <Navigation />

      <main>
        <section className="relative bg-[#F4F8FC] pt-40 md:pt-48 pb-16 md:pb-20 border-b border-[#E5EBF2] overflow-hidden">
          <div className="relative container-ags">
            <Link
              to="/"
              data-testid="contact-back-home"
              className="group inline-flex items-center gap-2 text-sm font-medium text-[#0E2A57] hover:text-[#2BB6E5] transition-colors mb-8"
            >
              <ArrowLeft size={16} className="transition-transform group-hover:-translate-x-1" />
              Back to home
            </Link>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-16 bg-[#2BB6E5]" />
              <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#0E2A57] font-semibold">
                Contact Us
              </span>
            </div>
            <h1 className="font-heading font-extrabold text-5xl md:text-7xl leading-[1.0] tracking-[-0.04em] text-[#0E2A57] max-w-4xl">
              We answer every enquiry.
            </h1>
            <p className="mt-6 text-[#475569] text-lg md:text-xl leading-relaxed max-w-2xl">
              Quotes, support, partnerships or just a quick question — the AGS family is one message away.
            </p>
          </div>
        </section>

        <section className="bg-white py-16 md:py-24">
          <div className="container-ags grid md:grid-cols-12 gap-10 md:gap-16">
            <div className="md:col-span-5">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-[#0E2A57] tracking-tight">
                Get in touch directly
              </h2>
              <ul className="mt-8 space-y-5 text-[#475569]">
                <li className="flex items-start gap-4">
                  <span className="bg-[#0E2A57] text-white w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <Phone size={16} />
                  </span>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.22em] text-[#737373] font-semibold">
                      Phone
                    </div>
                    <div className="font-semibold text-[#0E2A57] mt-0.5"><a href="tel:+353873816923" className="hover:underline">+353 87 381 6923</a></div>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="bg-[#0E2A57] text-white w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <Mail size={16} />
                  </span>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.22em] text-[#737373] font-semibold">
                      Email
                    </div>
                    <div className="font-semibold text-[#0E2A57] mt-0.5"><a href="mailto:info@ags-services.ie" className="hover:underline">info@ags-services.ie</a></div>
                  </div>
                </li>
              </ul>

              <div className="mt-10 pt-8 border-t border-[#E5EBF2] space-y-3 text-sm text-[#475569]">
                {[
                  "Reply within one business day",
                  "Nationwide coverage across Ireland",
                  "All enquiries treated in confidence",
                ].map((b) => (
                  <div key={b} className="flex items-center gap-3">
                    <Check size={14} strokeWidth={3} className="text-[#2BB6E5]" />
                    <span>{b}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="md:col-span-7">
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="bg-[#F4F8FC] border border-[#E5EBF2] p-6 md:p-10"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <label className="block">
                    <span className="block text-[11px] uppercase tracking-[0.18em] text-[#737373] font-semibold mb-2">
                      Full Name *
                    </span>
                    <input
                      name="full_name"
                      value={form.full_name}
                      onChange={onChange}
                      required
                      placeholder="Jane Murphy"
                      data-testid="contact-input-name"
                      className={inputClass}
                    />
                  </label>
                  <label className="block">
                    <span className="block text-[11px] uppercase tracking-[0.18em] text-[#737373] font-semibold mb-2">
                      Email *
                    </span>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={onChange}
                      required
                      placeholder="jane@company.ie"
                      data-testid="contact-input-email"
                      className={inputClass}
                    />
                  </label>
                  <label className="block">
                    <span className="block text-[11px] uppercase tracking-[0.18em] text-[#737373] font-semibold mb-2">
                      Phone
                    </span>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={onChange}
                      placeholder="+353 1 234 5678"
                      data-testid="contact-input-phone"
                      className={inputClass}
                    />
                  </label>
                  <label className="block">
                    <span className="block text-[11px] uppercase tracking-[0.18em] text-[#737373] font-semibold mb-2">
                      Subject *
                    </span>
                    <select
                      name="subject"
                      value={form.subject}
                      onChange={onChange}
                      data-testid="contact-input-subject"
                      className={inputClass}
                    >
                      {SUBJECTS.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block md:col-span-2">
                    <span className="block text-[11px] uppercase tracking-[0.18em] text-[#737373] font-semibold mb-2">
                      Message *
                    </span>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={onChange}
                      required
                      rows={6}
                      placeholder="Tell us how we can help…"
                      data-testid="contact-input-message"
                      className={`${inputClass} resize-none`}
                    />
                  </label>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
                  <p className="text-xs text-[#737373] max-w-sm">
                    By submitting, you agree we may contact you about your enquiry. See our Privacy Notice.
                  </p>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit"
                    className="group inline-flex items-center gap-3 bg-[#0E2A57] text-white hover:bg-[#081A37] disabled:opacity-60 disabled:cursor-not-allowed px-10 py-5 font-semibold tracking-wide transition-colors"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={18} className="animate-spin" /> Sending…
                      </>
                    ) : (
                      <>
                        Send Message
                        <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                      </>
                    )}
                  </button>
                </div>

                {sent && (
                  <div
                    data-testid="contact-success"
                    className="mt-6 border border-[#0E2A57]/30 bg-[#BEE7F5]/30 text-[#081A37] p-4 text-sm"
                  >
                    Thank you — your message has been received. We'll be in touch shortly.
                  </div>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
